import React, { Fragment, useEffect, useState } from 'react'
import ReportPage from '../../reports/reportPage.v2'
import TableWidgetSeparated from '@modul-connect/shared/components/molecules/tableWidget/tableWidgetSeparated.v2'
import { asset_details_columns, asset_details_controllers_columns, asset_overview_columns } from './assetColumns'
import BatteryLowIcon from '@modul-connect/shared/img/batteryLow.svg'
import BatteryHighIcon from '@modul-connect/shared/img/batteryHigh.svg'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth0 } from '@auth0/auth0-react'
import { getVehiclesWithSensors } from '../../../state/actions/vehicles'
import {TabbedSimpleTable} from '@modul-connect/shared/components/molecules/tableWidget/tabbedSimpleTable' 
import View from '@modul-connect/shared/components/atoms/view'
import Info from '@modul-connect/shared/img/info.svg'
import Popup from '@modul-connect/shared/img/popup.svg'
import { DateTime } from 'luxon'
import { BatteryGuideModal } from './batteryGuideModal'

const formatControllerType = (type) => {
  switch (type) {
    case 'display':
      return 'Display switch panel'
    case 'control_switch':
      return 'Control switch'
    case 'remote':
      return 'Remote control'
    default:
      return type ?? '-'
  }
}
    
const mapAssetsToRows = (
  list = [], 
  { typeLabel, showType = true, showName = true} = {}
) =>
  list.map(x => {

    const isConstantPower =
  x.batteryLevel === null &&
  ['display', 'control'].includes(x.type)

    const level =
    isConstantPower ? 'Constant power'
      : x.batteryLevel === null
        ? '-'
        : x.batteryLevel === 0
          ? 0 + '%'
          : `Below ${x.batteryLevel}%`

    const batteryIcon = x.batteryLevel == null ? null : x.batteryLevel <= 25 ? BatteryLowIcon : BatteryHighIcon

    return [
      {
        valueComponent: (
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 36,
              fontSize: '12px', 
              fontFamily: 'Inter',
              justifyContent:
                showType && showName
                  ? 'flex-start'
                  : 'center'
            }}
          >
            {showType && (
              <View style={{ display: 'flex', flexDirection: 'row', gap: 2}}>
                <span>{typeLabel}</span>
                <span style={{  textTransform: 'capitalize'}}>{formatControllerType(x.type ?? x.category ?? '-')}</span>
              </View>
            )}

            {showName && (
              <View style={{ display: 'flex', flexDirection: 'row', gap: 2  }}>
                <span>Name:</span>
                <span style={{  textTransform: 'capitalize'}}>{x.name ?? '-'}</span>
              </View>
            )}
          </View>
        ),
      },
      {
        value1: `Battery: ${level}`,
        value1Icon: batteryIcon,
        value2: `Last read: ${x.latestReading
          ? DateTime.fromISO(x.latestReading).toFormat('yyyy-MM-dd HH:mm')
          : '-'}`
      },
      {
        value: `Serial number: ${x.serialNumber}`,
      },
      {
        value:
          `Installed: ${
            x.installedAt
              ? String(x.installedAt).slice(0, 10)
              : '-'
          }`,
      },
    ]
  })

export const AssetOverview = () => {

  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth0()

  const loading = useSelector(state => state.loading)
  const vehicles = useSelector(state => state.vehiclesWithSensors)
  const selectedOrganisations = useSelector(state => state.selected_organisations)

  const [openModal, setOpenModal] = useState(null)

  useEffect(() => {
    getAccessTokenSilently()
      .then(token => {
        dispatch(getVehiclesWithSensors( token ))
      })
  }, [selectedOrganisations])


  const formatAssetSummaryCell = ({ lowBattery, total }, items = []) => {
    const hasRemote = items.some(x => x?.type === 'remote')
    const hasConstantPowerType = items.some(x => ['display', 'control'].includes(x?.type))

    const isConstantPower = lowBattery === null && hasConstantPowerType && !hasRemote

    const icon = isConstantPower
      ? null
      : lowBattery === true
        ? BatteryLowIcon
        : lowBattery === false
          ? BatteryHighIcon
          : null

    const batteryText = isConstantPower
      ? 'Constant power'
      : lowBattery === null
        ? 'Battery: N/A'
        : 'Battery:'

    return {
      value1: `Installed: ${total ?? '0'} pcs`,
      value2: batteryText,
      value2Icon: icon,
    }
  }
  const sortOrder = (val) => {
    if (val === true) return 0      
    if (val === false) return 1     
    return 2                        
  }

  const vehicleData = vehicles.map(vehicle => {
    const hasSensors = vehicle?.sensors?.data?.length > 0
    const hasTrackers = vehicle?.trackers?.data?.length > 0
    const hasControllers = vehicle?.controllers?.data?.length > 0

    const tabs = [
      {
        id: 'sensors',
        label: 'Sensor(s)',
        data: [
          ...(hasSensors ? [{
            isDivider: true,
            action: {
              label: 'How to change battery – Sensors',
              iconBefore: Info,
              iconAfter: Popup,
              onClick: () => setOpenModal('sensors'),
            },
          }] : []),
          ...mapAssetsToRows(vehicle?.sensors?.data ?? [], { typeLabel: 'Sensor type:' }),
        ],
        columns: asset_details_columns,
        noDataText: 'No sensors installed in the vehicle',
      },
      {
        id: 'trackers',
        label: 'Tracker(s)',
        data: [
          // ...(hasTrackers ? [{
          //   isDivider: true,
          //   action: {
          //     label: 'End-of-life handling',
          //     iconBefore: Info,
          //     iconAfter: Popup,
          //     onClick: () => setOpenModal('trackers'),
          //   },
          // }] : []),
          ...mapAssetsToRows(vehicle?.trackers?.data ?? [], { showType: false }),
        ],
        columns: asset_details_columns,
        noDataText: 'No trackers installed in the vehicle',
      },
      {
        id: 'controls',
        label: 'Controller(s)',
        data: [
          ...(hasControllers ? [{
            isDivider: true,
            action: {
              label: 'How to change battery – Remote control',
              iconBefore: Info,
              iconAfter: Popup,
              onClick: () => setOpenModal('controllers'),
            },
          }] : []),
          ...mapAssetsToRows(vehicle?.controllers?.data ?? [], {
            showType: true,
            showName: false,
            typeLabel: 'Control type:'
          }),
        ],
        columns: asset_details_columns,
        noDataText: 'No controllers installed in the vehicle',
      },
    ]


    return {
      id: vehicle.mainbox_id,
      vehicleName: vehicle.vehicleName,

      sensors: formatAssetSummaryCell(vehicle.sensors),
      sensors_sort: sortOrder(vehicle?.sensors?.lowBattery),

      trackers: formatAssetSummaryCell(vehicle.trackers),
      trackers_sort: sortOrder(vehicle?.trackers?.lowBattery),

      controls: formatAssetSummaryCell(vehicle.controllers, vehicle?.controllers?.data ?? []),
      controls_sort: sortOrder(vehicle?.controllers?.lowBattery),

      organisationIds: vehicle.organisationIds,

      isExpandable: true,
      onExpand: () => {},
      collapsibleComponent: (
        <TabbedSimpleTable
          tabs={tabs}
          defaultTab={0}
        />
      ),
    } 
  })

  const vehicleTable =
    <TableWidgetSeparated
      data={vehicleData}
      columns={asset_overview_columns}
      totalItems={vehicleData.length}
      themes={'themes'}
      isSortable
      defaultSort='vehicleName'
      defaultSortOrder='asc'
      fixedLayout
    />
  return (
    <Fragment>
      <ReportPage
        title='Asset Overview'
        noSearch
        tables={vehicleTable}
        isLoading={loading.vehicleWithSensors === 'loading'}
      >
        <BatteryGuideModal openKey={openModal} onClose={() => setOpenModal(null)} />
      </ReportPage>
    </Fragment>
  )
}